import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  FaChartLine, 
  FaCalendarAlt, 
  FaBoxOpen, 
  FaCheckCircle, 
  FaClock, 
  FaMoneyBillWave,
  FaMotorcycle,
  FaTruck,
  FaSpinner
} from 'react-icons/fa';
import axios from '../../../lib/axios';
import Container from './styles';

const Statistics = () => {
  const [dateRange, setDateRange] = useState('7days');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalOrders: 0,
    deliveredOrders: 0,
    pendingOrders: 0,
    cancelledOrders: 0,
    successRate: 0,
    avgDeliveryTime: 0,
    totalRevenue: 0,
    activeRiders: 0
  });
  const [ordersByStatus, setOrdersByStatus] = useState([]);
  const [topRiders, setTopRiders] = useState([]);

  useEffect(() => {
    fetchStatistics();
  }, [dateRange]);

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      // Fetch orders data
      const ordersResponse = await axios.get(`/order?page=1&limit=1000`);
      const orders = ordersResponse.data || [];

      // Calculate statistics
      const delivered = orders.filter(o => o.status === 'DELIVERED').length;
      const pending = orders.filter(o => ['PENDING', 'ASSIGNED', 'PICKED_UP', 'IN_TRANSIT'].includes(o.status)).length;
      const cancelled = orders.filter(o => o.status === 'CANCELLED').length;
      const total = orders.length;

      // Calculate success rate
      const successRate = total > 0 ? ((delivered / total) * 100).toFixed(1) : 0;

      // Calculate revenue (total delivery fees)
      const revenue = orders.reduce((sum, order) => sum + (parseFloat(order.deliveryFee) || 0), 0);

      // Group orders by status
      const statusGroups = [
        { status: 'DELIVERED', count: delivered, color: '#6ABA6D' },
        { status: 'IN_TRANSIT', count: orders.filter(o => o.status === 'IN_TRANSIT').length, color: '#FE7D11' },
        { status: 'PENDING', count: orders.filter(o => o.status === 'PENDING').length, color: '#FFA726' },
        { status: 'CANCELLED', count: cancelled, color: '#ef4444' }
      ];

      // Get rider performance
      const riderStats = {};
      orders.forEach(order => {
        if (order.riderId) {
          if (!riderStats[order.riderId]) {
            riderStats[order.riderId] = {
              id: order.riderId,
              name: order.riderName || 'Unknown Rider',
              completedOrders: 0,
              totalOrders: 0
            };
          }
          riderStats[order.riderId].totalOrders++;
          if (order.status === 'DELIVERED') {
            riderStats[order.riderId].completedOrders++;
          }
        }
      });

      const ridersArray = Object.values(riderStats)
        .map(rider => ({
          ...rider,
          successRate: rider.totalOrders > 0 ? ((rider.completedOrders / rider.totalOrders) * 100).toFixed(1) : 0
        }))
        .sort((a, b) => b.completedOrders - a.completedOrders)
        .slice(0, 5);

      setStats({
        totalOrders: total,
        deliveredOrders: delivered,
        pendingOrders: pending,
        cancelledOrders: cancelled,
        successRate,
        avgDeliveryTime: 28, // This would need actual calculation from timestamps
        totalRevenue: revenue,
        activeRiders: new Set(orders.filter(o => o.riderId).map(o => o.riderId)).size
      });

      setOrdersByStatus(statusGroups);
      setTopRiders(ridersArray);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₦${amount.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <Container>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <FaSpinner style={{ fontSize: '3rem', color: '#FE7D11', animation: 'spin 1s linear infinite' }} />
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <header>
        <div className="top--row">
          <h2>Delivery Statistics</h2>
        </div>
        <div className="header--row">
          <select 
            value={dateRange} 
            onChange={(e) => setDateRange(e.target.value)}
            className="date-select"
          >
            <option value="today">Today</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </header>

      <div className="stats--container">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(106, 186, 109, 0.15)', color: '#6ABA6D' }}>
            <FaBoxOpen />
          </div>
          <h3>Total Orders</h3>
          <p className="stat-value">{stats.totalOrders.toLocaleString()}</p>
          <p className="stat-detail">
            {stats.deliveredOrders} delivered • {stats.pendingOrders} in progress
          </p>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(106, 186, 109, 0.15)', color: '#6ABA6D' }}>
            <FaCheckCircle />
          </div>
          <h3>Success Rate</h3>
          <p className="stat-value">{stats.successRate}%</p>
          <p className="stat-detail">
            {stats.deliveredOrders} completed successfully
          </p>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(254, 125, 17, 0.15)', color: '#FE7D11' }}>
            <FaClock />
          </div>
          <h3>Avg Delivery Time</h3>
          <p className="stat-value">{stats.avgDeliveryTime} min</p>
          <p className="stat-detail">
            Average time to complete delivery
          </p>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(254, 125, 17, 0.15)', color: '#FE7D11' }}>
            <FaMoneyBillWave />
          </div>
          <h3>Total Revenue</h3>
          <p className="stat-value">{formatCurrency(stats.totalRevenue)}</p>
          <p className="stat-detail">
            Total delivery fees collected
          </p>
        </div>
      </div>

      <div className="charts--container">
        <div className="chart-card">
          <h3>Orders by Status</h3>
          <div className="status-chart">
            {ordersByStatus.map((item, index) => (
              <div key={index} className="status-item">
                <div className="status-info">
                  <div className="status-color" style={{ background: item.color }}></div>
                  <span className="status-label">{item.status}</span>
                </div>
                <div className="status-stats">
                  <span className="status-count">{item.count}</span>
                  <span className="status-percentage">
                    {stats.totalOrders > 0 ? ((item.count / stats.totalOrders) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <div className="status-bar">
                  <div 
                    className="status-bar-fill" 
                    style={{ 
                      width: `${stats.totalOrders > 0 ? (item.count / stats.totalOrders) * 100 : 0}%`,
                      background: item.color
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="chart-card">
          <h3>Top Performing Riders</h3>
          <div className="riders-list">
            {topRiders.length > 0 ? (
              topRiders.map((rider, index) => (
                <div key={rider.id} className="rider-item">
                  <div className="rider-rank" style={{ 
                    background: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : 'rgba(255, 255, 255, 0.1)',
                    color: index < 3 ? '#1A1D31' : '#fff'
                  }}>
                    #{index + 1}
                  </div>
                  <div className="rider-info">
                    <FaMotorcycle style={{ color: '#FE7D11', marginRight: '0.5em' }} />
                    <div>
                      <div className="rider-name">{rider.name}</div>
                      <div className="rider-stats">
                        {rider.completedOrders} completed • {rider.successRate}% success
                      </div>
                    </div>
                  </div>
                  <div className="rider-badge">
                    {rider.completedOrders} orders
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <FaMotorcycle style={{ fontSize: '2rem', opacity: 0.3, marginBottom: '1em' }} />
                <p>No rider data available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="additional-stats">
        <div className="stat-card-small">
          <FaMotorcycle className="small-icon" />
          <div>
            <p className="small-label">Active Riders</p>
            <p className="small-value">{stats.activeRiders}</p>
          </div>
        </div>
        
        <div className="stat-card-small">
          <FaTruck className="small-icon" />
          <div>
            <p className="small-label">In Transit</p>
            <p className="small-value">{ordersByStatus.find(s => s.status === 'IN_TRANSIT')?.count || 0}</p>
          </div>
        </div>
        
        <div className="stat-card-small">
          <FaBoxOpen className="small-icon" />
          <div>
            <p className="small-label">Pending</p>
            <p className="small-value">{stats.pendingOrders}</p>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default Statistics;
